import type { InputOrbitRow } from "../services/inputs";
import { InputsState, type InputsStateType } from "../types/inputs";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const inputStatePillClassName: Record<InputsStateType, string> = {
  ACTIVE:
    "bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-200",
  ABOUT_TO_EXPIRE:
    "bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200 hover:bg-amber-200",
  EXPIRED:
    "bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-200",
  TEST: "bg-blue-200 ring-1 ring-inset ring-slate-200 hover:bg-blue-300",
};

export const inputStateSelectedClassName: Record<InputsStateType, string> = {
  ACTIVE: "bg-emerald-200 text-emerald-900 ring-emerald-300",
  ABOUT_TO_EXPIRE: "bg-amber-200 text-amber-900 ring-amber-300",
  EXPIRED: "bg-slate-200 text-slate-800 ring-slate-300",
  TEST: "bg-blue-200 text-slate-800 ring-slate-300",
};

const getWrappedValue = (value: unknown) => {
  if (value && typeof value === "object" && "value" in value) {
    return (value as { value?: unknown }).value;
  }

  return value;
};

const getDayStart = (date = new Date()) => {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);

  return dayStart.getTime();
};

export const getComputedInputState = (
  row: InputOrbitRow,
  now = new Date(),
): InputsStateType | null => {
  const category = String(getWrappedValue(row.linkedOEM?.category) ?? "");

  if (category.toLowerCase() === "test") {
    return InputsState.TEST;
  }

  if (category !== "OPER" || row.current !== true) {
    return null;
  }

  const endDateTime = Date.parse(
    String(getWrappedValue(row.linkedOEM?.endDate) ?? ""),
  );

  if (!Number.isFinite(endDateTime)) {
    return null;
  }

  const todayStart = getDayStart(now);
  const tomorrowStart = todayStart + ONE_DAY_MS;

  if (endDateTime < todayStart) {
    return InputsState.EXPIRED;
  }

  if (endDateTime < tomorrowStart) {
    return InputsState.ABOUT_TO_EXPIRE;
  }

  return InputsState.ACTIVE;
};

export const InputStateBadge = ({
  state,
}: {
  state: InputsStateType | null;
}) => {
  if (!state) {
    return <span className="text-slate-400">-</span>;
  }

  return (
    <span
      className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold uppercase ring-1 ring-inset ${inputStateSelectedClassName[state]}`}
    >
      {state.toLowerCase().replaceAll("_", " ")}
    </span>
  );
};
